import React, { useState, useCallback, useRef, useEffect } from "react";

const CalculatorModal = ({ show, onClose, onApplyToTicket }) => {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [resetNext, setResetNext] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  useEffect(() => {
    if (show) {
      setDisplay("0");
      setPreviousValue(null);
      setOperation(null);
      setResetNext(false);
      setPosition({ x: 0, y: 0 });
    }
  }, [show]);

  const handlePointerDown = useCallback((e) => {
    if (e.target.closest("button")) return;
    const rect = modalRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setIsDragging(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };
    const handleUp = () => setIsDragging(false);
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [isDragging]);

  const handleDigit = useCallback((d) => {
    setDisplay((prev) => {
      if (resetNext || prev === "0") {
        setResetNext(false);
        return d;
      }
      return prev + d;
    });
  }, [resetNext]);

  const handleDecimal = useCallback(() => {
    setDisplay((prev) => {
      if (resetNext) {
        setResetNext(false);
        return "0.";
      }
      if (prev.includes(".")) return prev;
      return prev + ".";
    });
  }, [resetNext]);

  const calculate = useCallback((a, b, op) => {
    const x = parseFloat(a);
    const y = parseFloat(b);
    switch (op) {
      case "+": return x + y;
      case "-": return x - y;
      case "×": return x * y;
      case "÷": return y === 0 ? "Erreur" : x / y;
      default: return y;
    }
  }, []);

  const handleOperation = useCallback((op) => {
    const current = parseFloat(display);
    if (previousValue !== null && operation && !resetNext) {
      const result = calculate(previousValue, current, operation);
      setDisplay(String(result));
      setPreviousValue(result);
    } else {
      setPreviousValue(current);
    }
    setOperation(op);
    setResetNext(true);
  }, [display, previousValue, operation, resetNext, calculate]);

  const handleEquals = useCallback(() => {
    if (previousValue === null || !operation) return;
    const current = parseFloat(display);
    const result = calculate(previousValue, current, operation);
    setDisplay(String(result));
    setPreviousValue(null);
    setOperation(null);
    setResetNext(true);
  }, [display, previousValue, operation, calculate]);

  const handleClear = useCallback(() => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setResetNext(false);
  }, []);

  const handleClearEntry = useCallback(() => {
    setDisplay("0");
    setResetNext(false);
  }, []);

  const handlePercent = useCallback(() => {
    setDisplay((prev) => String(parseFloat(prev) / 100));
  }, []);

  const handleSign = useCallback(() => {
    setDisplay((prev) => {
      const v = parseFloat(prev);
      return String(v === 0 ? 0 : -v);
    });
  }, []);

  if (!show) return null;

  const buttons = [
    { label: "C", action: handleClear, className: "calc-btn--clear" },
    { label: "CE", action: handleClearEntry, className: "calc-btn--clear" },
    { label: "%", action: handlePercent, className: "calc-btn--op" },
    { label: "÷", action: () => handleOperation("÷"), className: "calc-btn--op" },
    { label: "7", action: () => handleDigit("7") },
    { label: "8", action: () => handleDigit("8") },
    { label: "9", action: () => handleDigit("9") },
    { label: "×", action: () => handleOperation("×"), className: "calc-btn--op" },
    { label: "4", action: () => handleDigit("4") },
    { label: "5", action: () => handleDigit("5") },
    { label: "6", action: () => handleDigit("6") },
    { label: "-", action: () => handleOperation("-"), className: "calc-btn--op" },
    { label: "1", action: () => handleDigit("1") },
    { label: "2", action: () => handleDigit("2") },
    { label: "3", action: () => handleDigit("3") },
    { label: "+", action: () => handleOperation("+"), className: "calc-btn--op" },
    { label: "±", action: handleSign },
    { label: "0", action: () => handleDigit("0") },
    { label: ".", action: handleDecimal },
    { label: "=", action: handleEquals, className: "calc-btn--equals" },
  ];

  const displayValue = display.length > 12 ? parseFloat(display).toPrecision(10) : display;

  return (
    <div
      ref={modalRef}
      style={{
        position: "fixed",
        top: position.y || undefined,
        left: position.x || undefined,
        right: position.x === 0 ? "20px" : undefined,
        bottom: position.y === 0 ? "80px" : undefined,
        zIndex: 9998,
        width: "280px",
        backgroundColor: "#1f2937",
        borderRadius: "16px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        onPointerDown={handlePointerDown}
        style={{
          cursor: "grab",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#111827",
          borderRadius: "16px 16px 0 0",
        }}
      >
        <span className="text-white fw-semibold" style={{ fontSize: "0.85rem" }}>
          <i className="bi bi-calculator me-2"></i>Calculatrice
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
            fontSize: "1.1rem",
            cursor: "pointer",
            padding: "0 4px",
          }}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Display */}
      <div style={{ padding: "12px 16px 8px", textAlign: "right" }}>
        <div
          className="text-white fw-bold text-end"
          style={{
            fontSize: "1.6rem",
            minHeight: "40px",
            lineHeight: "40px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayValue}
        </div>
        {operation && (
          <div style={{ color: "#6b7280", fontSize: "0.75rem", textAlign: "right" }}>
            {previousValue} {operation}
          </div>
        )}
      </div>

      {/* Buttons Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "6px",
          padding: "6px 10px 10px",
        }}
      >
        {buttons.map((btn) => (
          <button
            key={btn.label}
            onClick={btn.action}
            style={{
              height: "46px",
              borderRadius: "10px",
              border: "none",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              backgroundColor: btn.className === "calc-btn--op"
                ? "#374151"
                : btn.className === "calc-btn--equals"
                ? "#2563eb"
                : btn.className === "calc-btn--clear"
                ? "#4b5563"
                : "#1f2937",
              color: btn.className === "calc-btn--equals"
                ? "#ffffff"
                : btn.className === "calc-btn--op"
                ? "#60a5fa"
                : btn.className === "calc-btn--clear"
                ? "#f87171"
                : "#e5e7eb",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) => {
              const base = btn.className === "calc-btn--op" ? "#4b5563"
                : btn.className === "calc-btn--equals" ? "#3b82f6"
                : btn.className === "calc-btn--clear" ? "#6b7280"
                : "#374151";
              e.currentTarget.style.backgroundColor = base;
            }}
            onMouseLeave={(e) => {
              const base = btn.className === "calc-btn--op" ? "#374151"
                : btn.className === "calc-btn--equals" ? "#2563eb"
                : btn.className === "calc-btn--clear" ? "#4b5563"
                : "#1f2937";
              e.currentTarget.style.backgroundColor = base;
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Apply to ticket button */}
      <div style={{ padding: "0 10px 12px" }}>
        <button
          onClick={() => {
            const value = parseFloat(display);
            if (!isNaN(value) && value !== 0) {
              onApplyToTicket(value);
              onClose();
            }
          }}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "10px",
            border: "2px solid #2563eb",
            backgroundColor: "rgba(37,99,235,0.15)",
            color: "#60a5fa",
            fontWeight: "600",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          <i className="bi bi-cart-plus me-2"></i>Appliquer au ticket
        </button>
      </div>
    </div>
  );
};

export default CalculatorModal;
